/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

// cognito.ts - Handles Cognito authentication flow

export interface CognitoConfig {
  cognitoUserPoolId: string;
  cognitoAppClientId: string;
  cognitoDomain: string;
  backendEndpoint: string;
  backendUrl: string;
  appUrl: string;
  apiGatewayUrl?: string;
  phoneInventoryApiUrl?: string;
}

export interface UserInfo {
  sub: string;
  username: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
}

interface JWTClaims {
  sub: string;
  'cognito:username'?: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  iat?: number;
}

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

// Window.CONFIG is declared in types/global.d.ts

export class CognitoAuth {
  private config: CognitoConfig | null;
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: Date | null = null;

  constructor() {
    // First try to get config from window.CONFIG injected by CDK
    // Then try to load from .env via Vite's import.meta.env
    // Finally fall back to null
    this.config = window.CONFIG || this.loadConfigFromEnv();

    // Load tokens from session storage if available
    this.loadTokens();
  }

  // Load configuration from environment variables
  private loadConfigFromEnv(): CognitoConfig | null {
    try {
      // Check if we have the VITE_APP_CONFIG environment variable
      if (import.meta.env.VITE_APP_CONFIG) {
        // Parse the JSON string from the environment variable
        return JSON.parse(import.meta.env.VITE_APP_CONFIG as string) as CognitoConfig;
      }

      // Check for individual environment variables
      const config: Partial<CognitoConfig> = {};
      let hasEnvConfig = false;

      if (import.meta.env.VITE_COGNITO_USER_POOL_ID) {
        config.cognitoUserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_COGNITO_APP_CLIENT_ID) {
        config.cognitoAppClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_BACKEND_ENDPOINT) {
        config.backendEndpoint = import.meta.env.VITE_BACKEND_ENDPOINT as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_BACKEND_URL) {
        config.backendUrl = import.meta.env.VITE_BACKEND_URL as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_APP_URL) {
        config.appUrl = import.meta.env.VITE_APP_URL as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_COGNITO_DOMAIN) {
        config.cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string;
        hasEnvConfig = true;
      }

      if (import.meta.env.VITE_API_GATEWAY_URL) {
        config.apiGatewayUrl = import.meta.env.VITE_API_GATEWAY_URL as string;
      }

      if (import.meta.env.VITE_PHONE_INVENTORY_API_URL) {
        config.phoneInventoryApiUrl = import.meta.env.VITE_PHONE_INVENTORY_API_URL as string;
      }

      return hasEnvConfig ? (config as CognitoConfig) : null;
    } catch (error) {
      console.error('Error loading config from environment variables:', error);
      return null;
    }
  }

  // Load tokens from session storage
  private loadTokens(): void {
    this.accessToken = sessionStorage.getItem('accessToken');
    this.idToken = sessionStorage.getItem('idToken');
    this.refreshToken = sessionStorage.getItem('refreshToken');

    const expiryStr = sessionStorage.getItem('tokenExpiry');
    if (expiryStr) {
      this.tokenExpiry = new Date(parseInt(expiryStr, 10));
    }
  }

  // Save tokens to session storage
  private saveTokens(): void {
    if (this.accessToken) sessionStorage.setItem('accessToken', this.accessToken);
    if (this.idToken) sessionStorage.setItem('idToken', this.idToken);
    if (this.refreshToken) sessionStorage.setItem('refreshToken', this.refreshToken);
    if (this.tokenExpiry)
      sessionStorage.setItem('tokenExpiry', this.tokenExpiry.getTime().toString());
  }

  // Clear tokens from session storage
  clearTokens(): void {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('idToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('tokenExpiry');

    this.accessToken = null;
    this.idToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
  }

  // Parse JWT token and extract claims
  private parseJWT(token: string): JWTClaims | null {
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      // Add padding if needed
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      const decoded = atob(paddedPayload);
      return JSON.parse(decoded) as JWTClaims;
    } catch (error) {
      console.error('Error parsing JWT:', error);
      return null;
    }
  }

  // Get user information from ID token (or mock data in dev mode)
  getUserInfo(): UserInfo | null {
    // In dev mode with bypassed auth, return mock user
    if (import.meta.env.VITE_BYPASS_AUTH === 'true' && import.meta.env.VITE_DEV_MODE === 'true') {
      return {
        sub: import.meta.env.VITE_MOCK_USER_ID as string || 'dev-user-001',
        username: import.meta.env.VITE_MOCK_USERNAME as string || 'dev.user@ttec.com',
        email: import.meta.env.VITE_MOCK_USER_EMAIL as string || 'dev.user@ttec.com',
        name: import.meta.env.VITE_MOCK_USER_NAME as string || 'Dev User',
        given_name: (import.meta.env.VITE_MOCK_USER_NAME as string || 'Dev User').split(' ')[0],
        family_name: (import.meta.env.VITE_MOCK_USER_NAME as string || 'Dev User').split(' ')[1] || '',
      };
    }

    if (!this.idToken) return null;

    const claims = this.parseJWT(this.idToken);
    if (!claims) return null;

    return {
      sub: claims.sub,
      username:
        claims['cognito:username'] || claims.preferred_username || claims.email || 'unknown',
      email: claims.email,
      name: claims.name,
      given_name: claims.given_name,
      family_name: claims.family_name,
    };
  }

  // Get current user ID for API requests
  getUserId(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.username : null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    // In dev mode with bypassed auth, always return true
    if (import.meta.env.VITE_BYPASS_AUTH === 'true' && import.meta.env.VITE_DEV_MODE === 'true') {
      return true;
    }

    // First check if we have tokens
    if (!this.accessToken || !this.idToken) {
      return false;
    }

    // Then check if the token is expired
    if (this.tokenExpiry && new Date() > this.tokenExpiry) {
      return false;
    }

    return true;
  }

  // Get access token (for API calls)
  getAccessToken(): string | null {
    // In dev mode with bypassed auth, return a mock token
    if (import.meta.env.VITE_BYPASS_AUTH === 'true' && import.meta.env.VITE_DEV_MODE === 'true') {
      return 'mock-access-token-dev';
    }
    return this.accessToken;
  }

  // Get ID token (contains user info)
  getIdToken(): string | null {
    return this.idToken;
  }

  // Get configuration
  getConfig(): CognitoConfig | null {
    return this.config;
  }

  // Get WebSocket endpoint with authorization
  getWebSocketUrl(): string {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    // For local development, use the backend endpoint directly
    if (this.config.backendEndpoint && this.config.backendEndpoint.includes('localhost')) {
      let endpoint = this.config.backendEndpoint;
      if (endpoint.endsWith('/')) {
        endpoint = endpoint.slice(0, -1);
      }
      // Convert HTTP to WS for local development
      endpoint = endpoint.replace('http://', 'ws://');
      return endpoint + '/';
    }

    // Use CloudFront with /ws/ path for secure WebSocket connection
    let endpoint = this.config.appUrl || this.config.backendUrl || this.config.backendEndpoint;
    
    if (!endpoint) {
      throw new Error('No backend URL configured. Please set appUrl, backendUrl, or backendEndpoint in configuration.');
    }
    
    if (endpoint.endsWith('/')) {
      endpoint = endpoint.slice(0, -1);
    }

    // Convert HTTPS to WSS and add /ws/ path
    endpoint = endpoint.replace('https://', 'wss://').replace('http://', 'ws://');

    // Use path-based token instead of query parameter
    if (this.accessToken) {
      return `${endpoint}/ws/${encodeURIComponent(this.accessToken)}`;
    }

    return `${endpoint}/ws/`;
  }

  private isExchangingCode = false;

  // Handle authentication flow
  async handleAuth(): Promise<boolean> {
    // Check if we have an authorization code in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      // Prevent double exchange in React StrictMode
      if (this.isExchangingCode) {
        console.log('Code exchange already in progress, skipping...');
        return false;
      }
      
      this.isExchangingCode = true;
      
      // Remove the code from the URL immediately to prevent reuse
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Exchange code for tokens
      const success = await this.exchangeCodeForTokens(code);
      this.isExchangingCode = false;
      return success;
    } else if (this.isAuthenticated()) {
      // Already authenticated
      return true;
    } else {
      // Redirect to Cognito login
      this.redirectToLogin();
      return false;
    }
  }

  // Redirect to Cognito login page
  redirectToLogin(): void {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    // Use appUrl from config, or fall back to current origin for local dev
    const appUrl = this.config.appUrl || window.location.origin;
    const redirectUri = encodeURIComponent(appUrl);

    // Use the provided domain if available, otherwise generate it
    const cognitoDomain = this.config.cognitoDomain || this.getDefaultCognitoDomain();

    const loginUrl = `${cognitoDomain}/login?client_id=${this.config.cognitoAppClientId}&response_type=code&scope=email+openid+profile&redirect_uri=${redirectUri}`;

    console.log('Redirecting to login:', { appUrl, redirectUri, loginUrl });
    window.location.href = loginUrl;
  }

  // Get default Cognito domain hostname if not provided in config
  private getDefaultCognitoDomain(): string {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    const region = this.config.cognitoUserPoolId.split('_')[0] || 'us-east-1';
    return `https://${this.config.cognitoUserPoolId}.auth.${region}.amazoncognito.com`;
  }

  // Get Cognito domain hostname (either from config or default)
  private getCognitoHostname(): string {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    if (this.config.cognitoDomain) {
      // Extract hostname from the domain URL
      try {
        const url = new URL(this.config.cognitoDomain);
        return url.hostname;
      } catch (e) {
        console.warn('Invalid cognitoDomain in config, using default');
      }
    }

    // Fall back to default domain pattern
    const region = this.config.cognitoUserPoolId.split('_')[0] || 'us-east-1';
    return `${this.config.cognitoUserPoolId}.auth.${region}.amazoncognito.com`;
  }

  // Exchange authorization code for tokens
  private async exchangeCodeForTokens(code: string): Promise<boolean> {
    try {
      if (!this.config) {
        throw new Error('Configuration not loaded');
      }

      // Use appUrl from config, or fall back to current origin for local dev
      const redirectUri = this.config.appUrl || window.location.origin;
      const tokenEndpoint = this.config.cognitoDomain
        ? `${this.config.cognitoDomain}/oauth2/token`
        : `https://${this.getCognitoHostname()}/oauth2/token`;

      console.log('Token exchange:', { redirectUri, tokenEndpoint, clientId: this.config.cognitoAppClientId });

      const params = new URLSearchParams();
      params.append('grant_type', 'authorization_code');
      params.append('client_id', this.config.cognitoAppClientId);
      params.append('redirect_uri', redirectUri);
      params.append('code', code);

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Token exchange failed: ${response.status} ${response.statusText}`, errorText);
        return false;
      }

      const data = (await response.json()) as TokenResponse;

      // Set tokens from response
      this.accessToken = data.access_token;
      this.idToken = data.id_token;
      this.refreshToken = data.refresh_token;

      // Calculate expiry time
      if (data.expires_in) {
        this.tokenExpiry = new Date(Date.now() + data.expires_in * 1000);
      }

      // Save tokens to session storage
      this.saveTokens();

      return true;
    } catch (error) {
      console.error('Error exchanging code for tokens:', error);
      return false;
    }
  }

  // Log out the user
  logout(): void {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    // Clear tokens
    this.clearTokens();

    // Redirect to Cognito logout
    const redirectUri = encodeURIComponent(this.config.appUrl);

    // Use provided domain if available
    const logoutUrl = this.config.cognitoDomain
      ? `${this.config.cognitoDomain}/logout?client_id=${this.config.cognitoAppClientId}&logout_uri=${redirectUri}`
      : `https://${this.getCognitoHostname()}/logout?client_id=${
          this.config.cognitoAppClientId
        }&logout_uri=${redirectUri}`;

    window.location.href = logoutUrl;
  }
}

// Create singleton instance
let cognitoAuthInstance: CognitoAuth | null = null;

export function getCognitoAuth(): CognitoAuth {
  if (!cognitoAuthInstance) {
    cognitoAuthInstance = new CognitoAuth();
  }
  return cognitoAuthInstance;
}
